/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ReactNode, useEffect, useState, useRef, createContext, useContext } from "react";
import { User } from "oidc-client-ts";
import { useLoading } from "./LoadingContext";
import { useGlobalError } from "./ErrorContext";
import {
  createMockUser,
  MockAuthContextType,
  FALLBACK_AUTH,
} from "./authentication-mock.helpers";

const MockAuthContext = createContext<MockAuthContextType | undefined>(undefined);

/**
 * Returns the mock authentication context.
 * During HMR the provider may not be available yet; returns a loading fallback instead of throwing.
 *
 * @returns {MockAuthContextType} Authentication state and methods.
 */
export const useAuth = (): MockAuthContextType => {
  const context = useContext(MockAuthContext);
  if (!context) {
    return FALLBACK_AUTH;
  }
  return context;
};

interface MockAuthenticationProviderProps {
  children: ReactNode;
}

export const MockAuthenticationProvider: React.FC<MockAuthenticationProviderProps> = ({
  children,
}) => {
  const { addLoading, removeLoading } = useLoading((state) => state);
  const { setGlobalError } = useGlobalError((state) => state);
  const [user, setUser] = useState<User | null | undefined>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const hasInitialized = useRef(false);

  // Initialize mock user on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      addLoading();
      
      // Simulate async authentication check
      setTimeout(() => {
        const mockUser = createMockUser();
        setUser(mockUser);
        setIsAuthenticated(true);
        setIsLoading(false);
        removeLoading();
      }, 500); // Small delay to simulate real auth flow
    }
  }, [addLoading, removeLoading]);

  const signin = async (): Promise<void> => {
    addLoading();
    try {
      // Simulate signin delay
      await new Promise((resolve) => setTimeout(resolve, 300));
      const mockUser = createMockUser();
      setUser(mockUser);
      setIsAuthenticated(true);
    } catch (error) {
      setGlobalError({
        type: "authentication",
        message: "Failed to sign in",
        error: error as Error,
      });
    } finally {
      removeLoading();
    }
  };

  const signout = async (): Promise<void> => {
    addLoading();
    try {
      // Simulate signout delay
      await new Promise((resolve) => setTimeout(resolve, 200));
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      setGlobalError({
        type: "authentication",
        message: "Failed to sign out",
        error: error as Error,
      });
    } finally {
      removeLoading();
    }
  };

  const removeUser = async (): Promise<void> => {
    setUser(null);
    setIsAuthenticated(false);
  };

  const signinSilent = async (): Promise<User | null> => {
    const mockUser = createMockUser();
    setUser(mockUser);
    setIsAuthenticated(true);
    return mockUser;
  };

  const navigateToProtectedRoute = (path: string, navigate: (path: string) => void): void => {
    // In mock mode, user is always authenticated, so just navigate
    navigate(path);
  };

  const value: MockAuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    error: null,
    signin,
    signout,
    removeUser,
    signinSilent,
    navigateToProtectedRoute,
  };

  return <MockAuthContext.Provider value={value}>{children}</MockAuthContext.Provider>;
};

export default MockAuthenticationProvider;
